import PropTypes from 'prop-types'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Decimal from 'decimal.js'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { getBalance, getPrimaryEmailAddress, getPrimaryPhoneNumber } from 'models/ProfileInfo/selectors'
import redemptionsActions from 'models/Redemptions/actions'
import { CASH_REDEMPTION_IDS } from 'models/Rewards/constants'

import { EVENT_TYPE, STRING } from 'resources'
import { APP_URLS } from 'resources/urls'

import { intercomShowNewMessage } from 'services/Intercom'

import { sendAnalyticsEvent } from 'services/analytics'
import getDecimalValue from 'utils/get-decimal-value'
import formatCurrency from 'utils/format-currency'
import { convertAmountToPoints } from 'utils/format-points'

import RedemptionPromptModal from './RedemptionPromptModal'
import RedemptionSuccessModal from './RedemptionSuccessModal'
import CashAmountSelector from './CashAmountSelector'
import GiftCardAmountSelector from './GiftCardAmountSelector'

import { RedemptionSelectionContainer, RedemptionSelectionTitle } from './styles'

const recaptchaGiftCardAction = 'web_gift_card_redemption'
const recaptchaCashAction = 'web_cash_redemption'
const MODAL_CLOSE_TIMEOUT_MS = 300

const MODAL_TYPES = {
  REDEEM_PROMPT: 'REDEEM_PROMPT',
  REDEEM_SUCCESS: 'REDEEM_SUCCESS',
}

const RedemptionSelection = ({ reward }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const balance = useSelector(getBalance)
  const primaryEmailAddress = useSelector(getPrimaryEmailAddress)
  const primaryPhoneNumber = useSelector(getPrimaryPhoneNumber)

  const [redemptionDestination, setRedemptionDestination] = useState(() =>
    primaryEmailAddress && reward.id !== 'venmo' ? primaryEmailAddress : primaryPhoneNumber,
  )
  const [isRequesting, setIsRequesting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [openModalKey, setOpenModalKey] = useState(null)

  const maxValue = getDecimalValue(reward.redemptionValues[reward.redemptionValues.length - 1] || 0)
  const maxAmount = Decimal.min(balance, maxValue)
  const maxGiftCardAmount = useMemo(
    () => Decimal.max(...reward.redemptionValues.filter((val) => getDecimalValue(val).lte(maxAmount)), new Decimal(0)),
    [reward.redemptionValues, maxAmount],
  )
  const isCashRedemption = CASH_REDEMPTION_IDS.includes(reward.id)

  const [selectedAmount, setSelectedAmount] = useState(() => {
    if (isCashRedemption) {
      return maxAmount.toFixed(2)
    }
    return maxGiftCardAmount.toFixed(2)
  })

  const finalAmount = useMemo(() => {
    if (isCashRedemption) {
      return Decimal.max(0, getDecimalValue(selectedAmount, 0).minus(reward.fees || 0)).toFixed(2)
    }
    return selectedAmount
  }, [isCashRedemption, selectedAmount, reward.fees])

  useEffect(() => {
    if (reward.id === 'paypal') {
      sendAnalyticsEvent(EVENT_TYPE.redemptionPayPalLoad)
    } else if (reward.id === 'venmo') {
      sendAnalyticsEvent(EVENT_TYPE.redemptionVenmoLoad)
    } else {
      sendAnalyticsEvent(EVENT_TYPE.redemptionLoad)
    }
  }, [reward.id])

  // Callback for confirmed Giftcard/Cash Redemption
  const confirmRedemptionCallback = useCallback((isSuccess, errorResponse) => {
    setIsRequesting(false)
    if (!isSuccess) {
      if (errorResponse?.code === 'RecaptchaVerificationException') {
        sendAnalyticsEvent(EVENT_TYPE.captchaFail, { screenName: 'Redemption', score: errorResponse?.score })
        setErrorMessage(STRING.wallet.redemptions.recaptchaErrorMessage)
        intercomShowNewMessage(STRING.wallet.redemptions.recaptchaErrorSupport('redemption'))
      } else {
        setErrorMessage(STRING.wallet.redemptions.giftCardRedemption.redeemFailureMessage)
      }
      return
    }

    setOpenModalKey(null)
    setTimeout(() => setOpenModalKey(MODAL_TYPES.REDEEM_SUCCESS), MODAL_CLOSE_TIMEOUT_MS)
  }, [])

  // Functions for Gift Card Redemption
  const handleRequestGiftCardConfirmClick = useCallback(
    (recaptchaToken) => {
      setIsRequesting(true)
      setErrorMessage('')
      sendAnalyticsEvent(EVENT_TYPE.redemptionRequestConfirmed, { Amount: selectedAmount })
      dispatch(
        redemptionsActions.requestTremendousRedemption.init(
          selectedAmount,
          redemptionDestination,
          recaptchaToken,
          confirmRedemptionCallback,
        ),
      )
    },
    [dispatch, selectedAmount, redemptionDestination, confirmRedemptionCallback],
  )

  const handleRequestGiftCardRedemption = useCallback(async () => {
    try {
      const recaptchaToken = await executeRecaptcha(recaptchaGiftCardAction)
      handleRequestGiftCardConfirmClick(recaptchaToken)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[Recaptcha] Recaptcha error`, error)
      setErrorMessage(STRING.wallet.redemptions.recaptchaErrorMessage)
      intercomShowNewMessage(STRING.wallet.redemptions.recaptchaErrorSupport('redemption'))
    }
  }, [executeRecaptcha, handleRequestGiftCardConfirmClick])

  const handleRequestGiftCardClick = useCallback(() => {
    sendAnalyticsEvent(EVENT_TYPE.redemptionRequested, { Amount: selectedAmount })
    setOpenModalKey(MODAL_TYPES.REDEEM_PROMPT)
    setErrorMessage('')
  }, [selectedAmount])

  // Functions for Cash Redemption
  const handleRequestCashConfirmClick = useCallback(
    (recaptchaToken) => {
      setIsRequesting(true)
      setErrorMessage('')

      const eventPayload = {
        amount: finalAmount,
        amountFormatted: formatCurrency(finalAmount),
        pointsAmount: convertAmountToPoints(selectedAmount),
      }
      if (reward.id === 'paypal') {
        eventPayload.emailAddress = redemptionDestination
        sendAnalyticsEvent(EVENT_TYPE.redemptionPayPalRequestConfirmed, eventPayload)
      } else if (reward.id === 'venmo') {
        eventPayload.phoneNumber = redemptionDestination
        sendAnalyticsEvent(EVENT_TYPE.redemptionVenmoRequestConfirmed, eventPayload)
      }

      dispatch(
        redemptionsActions.requestExternalRedemption.init(
          selectedAmount,
          redemptionDestination,
          recaptchaToken,
          confirmRedemptionCallback,
        ),
      )
    },
    [dispatch, selectedAmount, finalAmount, reward.id, redemptionDestination, confirmRedemptionCallback],
  )

  const handleRequestCashRedemption = useCallback(async () => {
    try {
      const recaptchaToken = await executeRecaptcha(recaptchaCashAction)
      handleRequestCashConfirmClick(recaptchaToken)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[Recaptcha] Recaptcha error`, error)
      setErrorMessage(STRING.wallet.redemptions.recaptchaErrorMessage)
      intercomShowNewMessage(STRING.wallet.redemptions.recaptchaErrorSupport('redemption'))
    }
  }, [executeRecaptcha, handleRequestCashConfirmClick])

  const handleRequestCashClick = useCallback(() => {
    if (reward.id === 'paypal') {
      sendAnalyticsEvent(EVENT_TYPE.redemptionPayPalRequested)
    } else if (reward.id === 'venmo') {
      sendAnalyticsEvent(EVENT_TYPE.redemptionVenmoRequested)
    }
    setOpenModalKey(MODAL_TYPES.REDEEM_PROMPT)
    setErrorMessage('')
  }, [reward.id])

  const handleRedeemSuccessClose = () => {
    setOpenModalKey(null)
    history.push(APP_URLS.REDEMPTION_SOCIAL_SHARE)
  }

  const redemptionSelector = useMemo(() => {
    if (isCashRedemption) {
      return (
        <CashAmountSelector
          reward={reward}
          redemptionDestination={redemptionDestination}
          setRedemptionDestination={setRedemptionDestination}
          selectedAmount={selectedAmount}
          setSelectedAmount={setSelectedAmount}
          onRequest={handleRequestCashClick}
        />
      )
    }
    return (
      <GiftCardAmountSelector
        reward={reward}
        redemptionDestination={redemptionDestination}
        setRedemptionDestination={setRedemptionDestination}
        selectedAmount={selectedAmount}
        setSelectedAmount={setSelectedAmount}
        onRequest={handleRequestGiftCardClick}
      />
    )
  }, [
    isCashRedemption,
    reward,
    redemptionDestination,
    selectedAmount,
    handleRequestCashClick,
    handleRequestGiftCardClick,
  ])

  return (
    <RedemptionSelectionContainer>
      <RedemptionPromptModal
        isOpen={openModalKey === MODAL_TYPES.REDEEM_PROMPT}
        toggle={() => setOpenModalKey(null)}
        amountFormatted={formatCurrency(finalAmount)}
        onRequestRedemption={isCashRedemption ? handleRequestCashRedemption : handleRequestGiftCardRedemption}
        isRequestingRedemption={isRequesting}
        errorMessage={errorMessage}
        accountType={reward.id}
      />
      <RedemptionSuccessModal
        isOpen={openModalKey === MODAL_TYPES.REDEEM_SUCCESS}
        onRedeemSuccessClose={handleRedeemSuccessClose}
      />
      <RedemptionSelectionTitle>
        {STRING.wallet.redemptions.giftCardRedemption.redeemSelectionTitle}
      </RedemptionSelectionTitle>
      {redemptionSelector}
    </RedemptionSelectionContainer>
  )
}

RedemptionSelection.propTypes = {
  reward: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    categoryId: PropTypes.string.isRequired,
    isTop: PropTypes.bool.isRequired,
    imageUrl: PropTypes.string.isRequired,
    redemptionValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentPrice: PropTypes.string,
    dayChange: PropTypes.string,
    fees: PropTypes.string,
    sharePrice: PropTypes.string,
    minShares: PropTypes.number,
  }).isRequired,
}

export default RedemptionSelection
